import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, EventEmitter, Inject, Injectable, Input, OnChanges, OnInit, Output } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneTableService } from '../../../../components/kiene-table/kiene-table.service';
import { Untersuchungsmaterial } from '../../../untersuchungsmaterialien/untersuchungsmaterial';
import { Artikelstrukturelement, ArtikelstrukturelementFlatNode } from '../../probenbegleitschein';
import { MessageService } from '../../../../../../projects/kiene-core/src/lib/services/message-service/message.service';

@Injectable()
export class UntersuchungTreeDatabase {
    dataChange = new BehaviorSubject<Artikelstrukturelement[]>([]);

    get data(): Artikelstrukturelement[] {
        return this.dataChange.value;
    }

    set data(value: Artikelstrukturelement[]) {
        this.dataChange.next(value);
    }

    constructor() {
        this.initialize();
    }

    initialize() {}

    public initArtikelstrukturData(tree: Artikelstrukturelement[]) {
        const data = tree;
        this.dataChange.next(data);
    }

    /**
     * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
     * The return value is the list of `TodoItemNode`.
     */
    buildFileTree(obj: { [key: string]: any }, level: number): Artikelstrukturelement[] {
        return Object.keys(obj).reduce<Artikelstrukturelement[]>((accumulator, key) => {
            const value = obj[key];
            const node = new Artikelstrukturelement();
            node.untersuchung = key;

            if (value != null) {
                if (typeof value === 'object') {
                    node.kinder = this.buildFileTree(value, level + 1);
                } else {
                    node.untersuchung = value;
                }
            }

            return accumulator.concat(node);
        }, []);
    }
}

@Component({
    selector: 'kiene-untersuchung-tree',
    templateUrl: './untersuchung-tree.component.html',
    styleUrls: ['./untersuchung-tree.component.scss'],
})
export class UntersuchungTreeComponent implements OnInit, OnChanges {
    @Input('untersuchungsmaterial')
    untersuchungsmaterial: Untersuchungsmaterial;

    @Output('articleSelected') articleSelected = new EventEmitter<ArtikelstrukturelementFlatNode[]>();

    untersuchungsmaterialId: string;

    selectedNodes: ArtikelstrukturelementFlatNode[] = [];

    /** Map from flat node to nested node. This helps us finding the nested node to be modified */
    flatNodeMap = new Map<ArtikelstrukturelementFlatNode, Artikelstrukturelement>();

    /** Map from nested node to flattened node. This helps us to keep the same object for selection */
    nestedNodeMap = new Map<Artikelstrukturelement, ArtikelstrukturelementFlatNode>();

    treeControl: FlatTreeControl<ArtikelstrukturelementFlatNode>;

    treeFlattener: MatTreeFlattener<Artikelstrukturelement, ArtikelstrukturelementFlatNode>;

    dataSource: MatTreeFlatDataSource<Artikelstrukturelement, ArtikelstrukturelementFlatNode>;

    artikelstruktur: any[] = [];

    constructor(
        private readonly route: ActivatedRoute,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly api: KieneTableService,
        private readonly notification: MessageService,
        private readonly _database: UntersuchungTreeDatabase,
    ) {
        this.untersuchungsmaterialId = this.route.snapshot.paramMap.get('id');

        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
        this.treeControl = new FlatTreeControl<ArtikelstrukturelementFlatNode>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

        _database.dataChange.subscribe(data => {
            this.dataSource.data = data;
        });
    }

    ngOnChanges() {
        if (this.untersuchungsmaterial) {
            this.loadUntersuchungsmaterial();
        }
    }

    ngOnInit() {
        if (this.untersuchungsmaterial) {
            this.loadUntersuchungsmaterial();
        }
    }

    ngOnDestroy() {
        this._database.data = [];
    }

    loadUntersuchungsmaterial() {
        this.selectedNodes = [];
        this.artikelstruktur = this.untersuchungsmaterial.artikelstruktur;
        this.initUntersuchungsmaterial();
        this._database.initArtikelstrukturData(this.artikelstruktur);
        this.treeControl.expandAll();
    }

    initUntersuchungsmaterial() {
        if (!this.artikelstruktur) {
            this.artikelstruktur = [];
        }

        for (let a of this.artikelstruktur) {
            if (!a.kinder) {
                a.kinder = [];
            }
        }
    }

    getLevel = (node: ArtikelstrukturelementFlatNode) => node.level;

    isExpandable = (node: ArtikelstrukturelementFlatNode) => node.expandable;

    getChildren = (node: Artikelstrukturelement): Artikelstrukturelement[] => node.kinder;

    hasChild = (_: number, _nodeData: ArtikelstrukturelementFlatNode) => _nodeData.expandable && !_nodeData.artikel_id;

    isArtikelNode = (_: number, _nodeData: ArtikelstrukturelementFlatNode) =>
        (!_nodeData.untersuchung || _nodeData.untersuchung === '') && _nodeData.artikel_id && _nodeData.artikel_id > -1;

    /**
     * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
     */
    transformer = (node: Artikelstrukturelement, level: number) => {
        const existingNode = this.nestedNodeMap.get(node);
        const flatNode =
            existingNode && existingNode.untersuchung === node.untersuchung
                ? existingNode
                : new ArtikelstrukturelementFlatNode();
        flatNode.untersuchung = node.untersuchung;
        flatNode.artikel = node.artikel;
        flatNode.artikel_id = node.artikel_id;
        flatNode.lieferant = node.lieferant;
        flatNode.level = level;

        flatNode.expandable = !!node.kinder;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
        return flatNode;
    };

    save() {
        this.untersuchungsmaterial.artikelstruktur = this._database.data;
        this.api
            .updateEntity(this.apiBaseUrl + 'untersuchungsmaterial/update.php', this.untersuchungsmaterial)
            .subscribe(
                () => {
                    this.notification.infoMessage('Untersuchungsmaterial erfolgreich gespeichert!');
                },
                error => {
                    this.notification.errorMessage('Fehler beim Speichern: ', error);
                },
            );
    }

    isCheckboxChecked() {
        return false;
    }

    checkboxChanged(node: ArtikelstrukturelementFlatNode, event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedNodes.push(node);
        } else {
            const index = this.selectedNodes.indexOf(node);
            this.selectedNodes.splice(index, 1);
        }
        console.debug(this.selectedNodes.toString());
        this.articleSelected.emit(this.selectedNodes);
    }
}
